import {
  Menu,
  MenuDivider,
  MenuHeader,
  MenuItem,
  MenuRadioGroup,
  SubMenu,
} from "@szhsin/react-menu";
import { Fragment } from "react";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

import { removeConsecutiveDividers } from "./dropdown-helpers";
import { DropdownOptionType } from "./dropdown-types";

const Dropdown = ({
  options,
  className = "",
  children,
  align = "start",
  size = ButtonSize.large,
  disabled,
  menuClassName = "",
  ...props
}: {
  children?: JSX.Element;
  "aria-label"?: string;
  align?: "start" | "end";
  className?: string;
  options: DropdownOptionType[];
  disabled?: boolean;
  size?: ButtonSize;
  menuClassName?: string;
}) => {
  const link = useLink();
  const cleanedOptions = removeConsecutiveDividers(options);
  const handleStopPropagation = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleClickLink = (e: any) => {
    if (e.syntheticEvent && !e.syntheticEvent.metaKey) {
      e.syntheticEvent.preventDefault();
      e.syntheticEvent.stopPropagation();
      link.redirect(e.value);
    }
  };

  const renderOption = (option: DropdownOptionType, i: number) =>
    option.type === "header" ? (
      <MenuItem
        key={option.label}
        disabled
        className="text-xs text-gray-400 font-medium tracking-tight"
      >
        {titleCase(option.label)}
      </MenuItem>
    ) : option.type === "divider" ? (
      <MenuDivider key={i} />
    ) : option.type === "submenu" ? (
      <SubMenu key={`${i}-${option.label}`} label={option.label}>
        {option.options.map(renderOption)}
      </SubMenu>
    ) : option.type === "radio" ? (
      <Fragment key={`${i}-${option.label}`}>
        <MenuHeader>{option.label}</MenuHeader>
        <MenuRadioGroup
          value={option.value}
          onRadioChange={option.onChangeValue}
        >
          {option.options.map((subOption, subOptionIndex) => (
            <MenuItem
              disabled={subOption.disabled}
              key={String(subOption.value) || subOptionIndex}
              type="radio"
              value={subOption.value}
            >
              {titleCase(subOption.label)}
            </MenuItem>
          ))}
        </MenuRadioGroup>
      </Fragment>
    ) : option.type === "link" ? (
      <MenuItem
        key={i}
        disabled={option.disabled}
        value={option.to}
        onClick={handleClickLink}
        href={typeof option.to === "string" ? option.to : option.to.pathname}
        className={classNames(
          "block w-full text-left px-4 py-2 text-sm",
          "hover:bg-gray-100",
          option.textColor,
          !option.textColor && "text-gray-700 hover:text-gray-900",
          option.className,
        )}
      >
        <div className={option.labelClassName}>{option.label}</div>
        {option.description && (
          <div className="text-gray-500 text-xs tracking-tight">
            {option.description}
          </div>
        )}
      </MenuItem>
    ) : (
      <MenuItem
        key={i}
        disabled={option.disabled}
        className={classNames(
          "block w-full text-left px-4 py-2 text-sm",
          "hover:bg-gray-100",
          option.textColor,
          !option.textColor && "text-gray-700 hover:text-gray-900",
          option.disabled && "opacity-50",
          option.className,
        )}
        onClick={option.onClick}
      >
        <div className={option.labelClassName}>{option.label}</div>
        {option.description && (
          <div className="text-gray-500 text-xs tracking-tight">
            {option.description}
          </div>
        )}
      </MenuItem>
    );

  return (
    <Menu
      className="relative inline-block text-left text-sm not-prose divide-y z-dropdown tf-print-display-none max-w-72 fs-unmask"
      portal
      align={align}
      menuClassName={menuClassName}
      menuButton={
        children ? (
          children
        ) : (
          <Button
            disabled={disabled}
            onClick={handleStopPropagation}
            icon={getTopicflowIcon("horizontalDots")}
            size={size}
            theme={buttonTheme.iconGray}
            aria-label={props["aria-label"]}
            className={classNames("shrink-0 text-gray-400 rounded", className)}
          />
        )
      }
    >
      {cleanedOptions.map(renderOption)}
    </Menu>
  );
};

export default Dropdown;
